import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useEvents } from '../../contexts/EventContext';
import { ROUTES, STORAGE_KEYS } from '../../utils/constants';
import { formatDate, isDateTodayOrFuture } from '../../utils/helpers';
import { getCategoryColor, getCategoryLabel } from '../../types';
import api from '../../services/api';

const QRCheckInPage: React.FC = () => {
  const navigate = useNavigate();
  const { events, fetchEvents } = useEvents();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [scannerReady, setScannerReady] = useState(false);
  const scannerRef = useRef<any>(null);
  const scannerContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const upcomingEvents = events
    .filter((e) => e.isActive && isDateTodayOrFuture(e.date))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Handle QR code scan result
  const handleScanResult = useCallback(async (decodedText: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(decodedText)) {
      setSnackbar({ open: true, message: 'Invalid QR code', severity: 'error' });
      return;
    }

    // Try attendee check-in first (per-attendee QR codes)
    try {
      const res = await api.put(`/registrations/attendee/${decodedText}/check-in`);
      setSnackbar({
        open: true,
        message: `${res.data.familyMember?.name || 'Attendee'} checked in!`,
        severity: 'success',
      });
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Not an attendee ID - try as registration ID (backward compat)
        try {
          const res = await api.put(`/registrations/${decodedText}/check-in`);
          setSnackbar({
            open: true,
            message: `${res.data.user?.firstName} ${res.data.user?.lastName} - all attendees checked in!`,
            severity: 'success',
          });
        } catch (regErr: any) {
          setSnackbar({
            open: true,
            message: regErr.response?.data?.error || 'Check-in failed - registration not found',
            severity: 'error',
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: err.response?.data?.error || 'Check-in failed',
          severity: 'error',
        });
      }
    }
  }, []);

  // QR Scanner setup - Only on mobile/tablet
  useEffect(() => {
    if (!isMobile || !scannerContainerRef.current) {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
        setScannerReady(false);
      }
      return;
    }

    let mounted = true;

    const startScanner = async (scanner: any, cameraConfig: any) => {
      await scanner.start(
        cameraConfig,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        },
        (decodedText: string) => {
          handleScanResult(decodedText);
        },
        () => {},
      );

      // Store device ID from the active video track (no extra permission request)
      try {
        const videoElement = scannerContainerRef.current?.querySelector('video');
        const track = videoElement?.srcObject instanceof MediaStream
          ? videoElement.srcObject.getVideoTracks()[0]
          : null;
        const deviceId = track?.getSettings()?.deviceId;
        if (deviceId) {
          localStorage.setItem(STORAGE_KEYS.CAMERA_DEVICE_ID, deviceId);
        }
      } catch {
        // Ignore - device ID storage is best-effort
      }

      scannerRef.current = scanner;
      setScannerReady(true);
    };

    const initScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (!mounted || !scannerContainerRef.current) return;

        const scanner = new Html5Qrcode('qr-reader-universal');
        const storedDeviceId = localStorage.getItem(STORAGE_KEYS.CAMERA_DEVICE_ID);

        try {
          const cameraConfig: any = storedDeviceId
            ? { deviceId: { exact: storedDeviceId } }
            : { facingMode: 'environment' };
          await startScanner(scanner, cameraConfig);
        } catch {
          // Stored device ID may be stale - fall back to facingMode
          if (storedDeviceId) {
            localStorage.removeItem(STORAGE_KEYS.CAMERA_DEVICE_ID);
            await startScanner(scanner, { facingMode: 'environment' });
          } else {
            throw new Error('Camera start failed');
          }
        }
      } catch {
        setSnackbar({ open: true, message: 'Failed to start camera', severity: 'error' });
      }
    };

    initScanner();

    return () => {
      mounted = false;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
        setScannerReady(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, handleScanResult]);

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}
        sx={{ mb: 3 }}
      >
        Back to Dashboard
      </Button>

      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        QR Check-In
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {isMobile
          ? 'Scan any attendee QR code to check them in'
          : 'Select an event below to manage check-ins'}
      </Typography>

      {isMobile ? (
        /* Mobile: QR Scanner */
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'center' }}>
              <QrCodeScannerIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                Point the camera at an attendee's QR code
              </Typography>
            </Box>
            <Box
              ref={scannerContainerRef}
              sx={{ maxWidth: 500, mx: 'auto' }}
            >
              <div id="qr-reader-universal" />
              {!scannerReady && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : (
        /* Desktop: Event list linking to per-event check-in */
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Upcoming Events
            </Typography>

            {upcomingEvents.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No upcoming events to check in
              </Typography>
            ) : (
              <List disablePadding>
                {upcomingEvents.map((event) => (
                  <ListItem
                    key={event.id}
                    sx={{
                      px: 0,
                      borderBottom: '1px solid #e0e0e0',
                      '&:last-child': { borderBottom: 'none' },
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={() => navigate(`/admin/events/${event.id}/check-in`)}
                  >
                    <ListItemText
                      primary={event.title}
                      secondary={`${formatDate(event.date)} | ${event.location}`}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={getCategoryLabel(event.category)}
                        size="small"
                        sx={{
                          backgroundColor: getCategoryColor(event.category),
                          color: 'white',
                          mr: 1,
                        }}
                      />
                      <Chip
                        label={`${event.currentAttendees}/${event.capacity}`}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/events/${event.id}/check-in`);
                        }}
                      >
                        Check In
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QRCheckInPage;
